import postinggroupService from '../../../services/postinggroupService';
import filterService from '../../../services/filterService';

import {
    setPostingGroupLoading,
    setPostingGroupError,
    setPostingGroupList,
    setPostingGroupCurrent,
    setPostingGroupPagination,
    setPostingGroupSort,
    setPostingGroupFilters,
    setPostingGroupDefaultFilter,
    setPostingGroupSettings,
    clearPostingGroupCurrent,
    clearPostingGroupError,
    setPostingGroupFilterLoading,
    changePostingGroupFilterCondition,
    addPostingGroupFilterCondition,
    updatePostingGroupFilterConditionRule,
    updatePostingGroupFilterConditionValue,
    removePostingGroupFilterCondition,
    resetPostingGroupFiltersAndPagination
} from './postinggroupSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().postinggroup.isSettingsInitialized) {
            dispatch(setPostingGroupLoading(true));
            const response = await postinggroupService.getSettings();

            // Set the settings
            dispatch(setPostingGroupSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setPostingGroupDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setPostingGroupError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setPostingGroupLoading(false));
    }
};

export const fetchPostinggroups = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().postinggroup;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setPostingGroupLoading(true));
        const response = await postinggroupService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setPostingGroupList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchPostinggroups:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setPostingGroupError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setPostingGroupLoading(false));
    }
};

export const fetchPostingGroup = (id) => async (dispatch) => {
    try {
        dispatch(setPostingGroupLoading(true));
        const response = await postinggroupService.getOne(id);
        dispatch(setPostingGroupCurrent(response.data));
    } catch (error) {
        dispatch(setPostingGroupError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setPostingGroupLoading(false));
    }
};

export const createPostingGroup = (data) => async (dispatch) => {
    try {
        const response = await postinggroupService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updatePostingGroup = (id, data) => async (dispatch) => {
    try {
        const response = await postinggroupService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deletePostingGroup = (id) => async (dispatch) => {
    try {
        dispatch(setPostingGroupLoading(true));
        const response = await postinggroupService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setPostingGroupError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setPostingGroupLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setPostingGroupPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setPostingGroupSort(sort));
};

export const searchPostinggroups = (query) => (dispatch, getState) => {
    const currentFilters = getState().postinggroup.filters;
    dispatch(setPostingGroupFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setPostingGroupDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changePostingGroupFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addPostingGroupFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updatePostingGroupFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updatePostingGroupFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removePostingGroupFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setPostingGroupFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().postinggroup.settings;
            dispatch(setPostingGroupSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setPostingGroupFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setPostingGroupFilterLoading(false));
        dispatch(setPostingGroupError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetPostingGroupFiltersAndPagination());
};